import { Routes, Route, useLocation } from 'react-router-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { ShieldAlert } from 'lucide-react'
import { useAuth } from './auth/AuthContext.jsx'
import useTrackPageView from './analytics/useTrackPageView.js'
import Navbar from './components/layout/Navbar.jsx'
import AllocationTable from './pages/AllocationTable.jsx'
import ActionCenter from './pages/ActionCenter.jsx'
import DoneActions from './pages/DoneActions.jsx'
import AuditLog from './pages/AuditLog.jsx'
import StoreList from './pages/StoreList.jsx'
import StoreDetail from './pages/StoreDetail.jsx'
import Financials from './pages/Financials.jsx'
import Prediction from './pages/Prediction.jsx'
import Logs from './pages/Logs.jsx'
import Login from './pages/Login.jsx'

const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.35, ease: 'easeOut' } },
  exit: { opacity: 0, y: -10, transition: { duration: 0.2, ease: 'easeIn' } },
}

function AnimatedPage({ children }) {
  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      {children}
    </motion.div>
  )
}

export default function App() {
  const location = useLocation()
  const { user, ready, forbidden } = useAuth()
  useTrackPageView()

  // Wait for localStorage check before rendering anything
  if (!ready) return null

  // Show login page for unauthenticated users
  if (!user) return <Login />

  // Authenticated but not on the allowlist
  if (forbidden) return (
    <div className="min-h-screen bg-bg-base flex items-center justify-center p-8">
      <div className="glass-card p-8 text-center max-w-md">
        <ShieldAlert size={48} className="text-amber-400 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-slate-200 mb-3">Access denied</h2>
        <p className="text-slate-400 text-sm mb-1">
          <span className="text-slate-300 font-medium">{user.email}</span> is authenticated
          but lacks permissions to use this app.
        </p>
        <p className="text-slate-400 text-sm">
          Please contact{' '}
          <a href="mailto:support@cerebi.ai" className="text-cyan-400 hover:underline">
            support@cerebi.ai
          </a>
          {' '}to request access.
        </p>
      </div>
    </div>
  )

  return (
    <div className="min-h-screen bg-bg-base text-slate-100 font-sans">
      <Navbar />
      <main className="pt-16">
        <AnimatePresence mode="wait">
          <Routes location={location} key={location.pathname}>
            <Route
              path="/"
              element={
                <AnimatedPage>
                  <Prediction />
                </AnimatedPage>
              }
            />
            <Route
              path="/allocation"
              element={
                <AnimatedPage>
                  <AllocationTable />
                </AnimatedPage>
              }
            />
            <Route
              path="/actions"
              element={
                <AnimatedPage>
                  <ActionCenter />
                </AnimatedPage>
              }
            />
            <Route
              path="/actions/done"
              element={
                <AnimatedPage>
                  <DoneActions />
                </AnimatedPage>
              }
            />
            <Route
              path="/stores"
              element={
                <AnimatedPage>
                  <StoreList />
                </AnimatedPage>
              }
            />
            <Route
              path="/stores/:id"
              element={
                <AnimatedPage>
                  <StoreDetail />
                </AnimatedPage>
              }
            />
            <Route
              path="/financials"
              element={
                <AnimatedPage>
                  <Financials />
                </AnimatedPage>
              }
            />
            <Route
              path="/audit"
              element={
                <AnimatedPage>
                  <AuditLog />
                </AnimatedPage>
              }
            />
            <Route
              path="/logs"
              element={
                <AnimatedPage>
                  <Logs />
                </AnimatedPage>
              }
            />
          </Routes>
        </AnimatePresence>
      </main>
    </div>
  )
}
